/**
 * useSubscriptions
 * ================
 * React Query replacement for the `fetchSubscriptions` async thunk and the
 * `subscriptionAdapter` entity adapter in commonSlice.
 *
 * The hook fetches the full list of Azure subscriptions via the shared
 * `resourcesProvider` (OneService CQRS) and normalises the result into the
 * same `Subscription` shape the old Redux code produced so every consumer
 * can migrate without changing its data expectations.
 *
 * React Query's built-in caching replaces the old thunk `condition` guard
 * that prevented duplicate in-flight requests.
 */

import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '~services/queryKeys';
import { handleQueryError } from '~services/queryUtils';
import { resourcesProvider } from '~services/providers';
import type { Subscription } from '~types/commonTypes';

async function fetchSubscriptions(): Promise<Subscription[]> {
  const entries = await resourcesProvider.listSubscriptions();
  // SubscriptionEntry from the CQRS provider is a superset of the fields the
  // old subscription adapter stored. Cast to our domain type.
  return entries as unknown as Subscription[];
}

export function useSubscriptions() {
  return useQuery({
    queryKey: queryKeys.subscriptions.lists(),
    queryFn: fetchSubscriptions,
    meta: { onError: handleQueryError },
  });
}
